let instance;
class WebSocketParserService {
  constructor() {
    if (instance) return instance;
    instance = this;
    this.connections = new Map(); 
    this.handlers = new Map();
    this.WS_URL_BASE = `${process.env.REACT_APP_WEB_SOCKET}`;
  }

  on(eventName, callback) {
    if (!this.handlers.has(eventName)) this.handlers.set(eventName, []);
    const list = this.handlers.get(eventName);
    if (!list.includes(callback)) list.push(callback);
    return () => {
      const idx = list.indexOf(callback);
      if (idx > -1) list.splice(idx, 1);
    };
  }

  emit(eventName, data) {
    if (this.handlers.has(eventName)) {
      this.handlers.get(eventName).forEach(cb => {
        try {
          cb(data);
        } catch (err) {
          console.error(`Error in handler for ${eventName}:`, err);
        }
      });
    }
  }

  connect(connectionId) {
    if (!this.WS_URL_BASE) {
      this.emit('error', { message: 'WebSocket base URL not configured.' });
      return;
    }

    // Avoid duplicate connections
    if (this.connections.has(connectionId)) {
      console.log(`⚠️ WebSocket already active for ${connectionId}`);
      return;
    }

    const wsUrl = `${this.WS_URL_BASE}/${connectionId}`;
    const ws = new WebSocket(wsUrl);
    this.connections.set(connectionId, ws);

    console.log(`🔗 Connecting WebSocket for ID: ${connectionId}`);

    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error(`⏱️ Timeout for ${connectionId}`);
        this.emit('error', { id: connectionId, message: 'Connection timeout.' });
        this.disconnect(connectionId);
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      console.log(`✅ WebSocket connected for ${connectionId}`);
      this.emit('open', { id: connectionId });
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log(`📩 [${connectionId}] Message:`, msg);

        switch (msg.profile_status) {
          case 'IN_PROGRESS':
            this.emit('progress', { id: connectionId, data: msg });
            break;
          case 'SUCCESS':
            this.emit('success', { id: connectionId, data: msg });
            break;
          case 'COMPLETE':
            this.emit('complete', { id: connectionId, data: msg });
            this.disconnect(connectionId);
            break;
          case 'FAILED':
            this.emit('failed', { id: connectionId, data: msg });
            this.disconnect(connectionId);
            break;
          case 'NOT_FOUND':
            this.emit('complete', { id: connectionId, data: msg });
            this.disconnect(connectionId);
            break;
          default:
            console.warn(`⚠️ Unknown status for ${connectionId}:`, msg.profile_status);
        }
      } catch (err) {
        console.error(`❌ Invalid message for ${connectionId}:`, err);
        this.emit('error', { id: connectionId, message: 'Invalid message.' });
      }
    };

    ws.onerror = (error) => {
      console.error(`🚨 WebSocket error for ${connectionId}:`, error);
      this.emit('error', { id: connectionId, message: 'Connection error.' });
    };

    ws.onclose = (event) => {
      console.log(`🔌 WebSocket closed for ${connectionId}:`, event.reason);
      this.connections.delete(connectionId);
      this.emit('close', { id: connectionId, message: 'Connection closed.' });
    };
  }

  disconnect(connectionId) {
    const ws = this.connections.get(connectionId);
    if (ws) {
      console.log(`🛑 Closing WebSocket for ${connectionId}`);
      try {
        ws.close(1000, 'Normal Closure');
      } catch (err) {
        console.warn(`Error closing socket ${connectionId}:`, err);
      }
      this.connections.delete(connectionId);
    }
  }

  disconnectAll() {
    console.log('🧹 Closing all WebSockets...');
    for (const id of this.connections.keys()) {
      this.disconnect(id);
    }
  }

  clearAllHandlers() {
    this.handlers.clear();
  }
}

const webSocketParserService = new WebSocketParserService();
export default webSocketParserService;